import { FunctionComponent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";

import { LeadRelationship } from "@signalist/shared-types";

import { Spinner } from "@/components/Spinner/Spinner";

import { LeadLabelColor } from "./LeadColorLabels";
import { getInitials } from "./leadRelationshipFormat";
import {
	ActiveCampaignSection,
	ConnectionsSection,
	OtherListsSection,
	PastOutreachSection,
} from "./LeadRelationshipSections";

/**
 * Consolidated lead relationship hover (PREC-3547). Renders the lead's identity header plus the
 * sections that exist on the row — active campaign (red) → past outreach (yellow) → LinkedIn
 * connections (blue) → other lists (grey) — fetched on demand. The section matching the hovered
 * dot (`highlightColor`) is scrolled into view and briefly flashed.
 *
 * "Review chat" is context-aware: lead-card surfaces pass `onReviewChat` (select + open chat tab);
 * table surfaces pass `reviewChatHref` (opens the lead's chat like the row's paper-plane action).
 */
export const LeadRelationshipPopover: FunctionComponent<{
	data: LeadRelationship | undefined;
	isLoading: boolean;
	isError: boolean;
	highlightColor: LeadLabelColor;
	reviewChatHref?: string;
	onReviewChat?: () => void;
}> = ({ data, isLoading, isError, highlightColor, reviewChatHref, onReviewChat }) => {
	const { t } = useTranslation();
	const highlightRef = useRef<HTMLDivElement>(null);

	// Scroll the hovered dot's section into view and flash it once content is in.
	useEffect(() => {
		const node = highlightRef.current;

		if (!node) {
			return;
		}

		node.scrollIntoView({ block: "nearest" });
		node.classList.add("bg-brand-white");
		const timer = setTimeout(() => node.classList.remove("bg-brand-white"), 900);

		return () => clearTimeout(timer);
	}, [data, highlightColor]);

	const hasActive = !!data?.activeCampaign;
	const hasPast = (data?.pastOutreach.length ?? 0) > 0;
	const hasConnections = (data?.connections.length ?? 0) > 0;
	const hasOtherLists = (data?.otherLists.length ?? 0) > 0;

	const sectionRef = (color: LeadLabelColor) =>
		color === highlightColor ? highlightRef : undefined;

	return (
		<div className="w-[340px] -m-1">
			<LeadHeader
				data={data}
				reviewChatHref={reviewChatHref}
				onReviewChat={onReviewChat}
				disabled={isLoading || isError || !data}
			/>

			{isLoading && (
				<div className="flex items-center justify-center gap-2 py-6 text-sm text-text-meta">
					<Spinner color="#4D66F5" />
					{t("inbox.relationshipHover.loading")}
				</div>
			)}

			{!isLoading && isError && (
				<div className="py-6 text-center text-sm text-status-error">
					{t("inbox.relationshipHover.error")}
				</div>
			)}

			{!isLoading && !isError && data && (
				<div className="flex flex-col divide-y divide-gray-100">
					{hasActive && (
						<div ref={sectionRef("red")} className="transition-colors">
							<ActiveCampaignSection data={data.activeCampaign!} />
						</div>
					)}
					{hasPast && (
						<div ref={sectionRef("yellow")} className="transition-colors">
							<PastOutreachSection rows={data.pastOutreach} />
						</div>
					)}
					{hasConnections && (
						<div ref={sectionRef("blue")} className="transition-colors">
							<ConnectionsSection rows={data.connections} />
						</div>
					)}
					{hasOtherLists && (
						<div ref={sectionRef("grey")} className="transition-colors">
							<OtherListsSection rows={data.otherLists} />
						</div>
					)}
					{!hasActive && !hasPast && !hasConnections && !hasOtherLists && (
						<div className="py-5 text-center text-sm text-text-meta">
							{t("inbox.relationshipHover.empty")}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const LeadHeader: FunctionComponent<{
	data: LeadRelationship | undefined;
	reviewChatHref?: string;
	onReviewChat?: () => void;
	disabled: boolean;
}> = ({ data, reviewChatHref, onReviewChat, disabled }) => {
	const { t } = useTranslation();
	const name = data?.lead.name ?? "";
	const sub = [data?.lead.title, data?.lead.company].filter(Boolean).join(" · ");

	return (
		<div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-gray-100">
			{data?.lead.avatarUrl ? (
				<img
					alt={name}
					src={data.lead.avatarUrl}
					className="w-8 h-8 rounded-full object-cover shrink-0 bg-gray-100"
				/>
			) : (
				<div className="w-8 h-8 rounded-full bg-blue-action text-white text-xs font-bold flex items-center justify-center shrink-0">
					{getInitials(name)}
				</div>
			)}
			<div className="min-w-0 flex-1">
				<div className="text-sm font-bold text-text truncate">{name}</div>
				{sub && <div className="text-xs text-text-meta truncate">{sub}</div>}
			</div>
			<ReviewChatAction
				reviewChatHref={reviewChatHref}
				onReviewChat={onReviewChat}
				disabled={disabled}
				label={t("inbox.relationshipHover.reviewChat")}
			/>
		</div>
	);
};

const ReviewChatAction: FunctionComponent<{
	reviewChatHref?: string;
	onReviewChat?: () => void;
	disabled: boolean;
	label: string;
}> = ({ reviewChatHref, onReviewChat, disabled, label }) => {
	const className =
		"inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-brand-light text-brand-light text-xs font-semibold hover:bg-brand-light hover:text-white transition-colors shrink-0 cursor-pointer";

	if (onReviewChat) {
		return (
			<button type="button" className={className} disabled={disabled} onClick={onReviewChat}>
				<Icon className="w-3 h-3" icon={faPaperPlane} />
				{label}
			</button>
		);
	}

	if (reviewChatHref) {
		return (
			<a className={className} href={reviewChatHref} target="_blank" rel="noreferrer">
				<Icon className="w-3 h-3" icon={faPaperPlane} />
				{label}
			</a>
		);
	}

	return null;
};
